import { useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { tx, txt, useContent } from "@/content/ContentContext";

// OTP password reset: request a 6-digit code (WhatsApp + email), then enter it with a new password.
export function ForgotPassword({ initial = "", onDone, onBack }) {
  useContent();
  const [step, setStep] = useState("ask");
  const [ident, setIdent] = useState(initial);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); } };
  const ask = (e) => { e.preventDefault(); run(async () => { const { data } = await api.post("/auth/forgot", { identifier: ident.trim() }); setMsg(data.detail); setStep("code"); }); };
  const reset = (e) => {
    e.preventDefault();
    if (pw.length < 6) return setErr(tx("login.fp_pw_short"));
    if (pw !== pw2) return setErr(tx("login.fp_pw_mismatch"));
    run(async () => { await api.post("/auth/reset", { identifier: ident.trim(), code: code.trim(), password: pw }); onDone(ident.trim()); });
  };
  const btn = "flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white transition-colors hover:bg-slate-800 disabled:opacity-50";
  return (
    <div className="space-y-5" data-testid="forgot-password">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="forgot-back"><ArrowLeft className="h-4 w-4" />{txt("login.fp_back")}</button>
      <h3 className="font-display text-xl font-bold text-slate-900">{txt("login.fp_title")}</h3>
      {step === "ask" ? (
        <form onSubmit={ask} className="space-y-4">
          <p className="text-sm text-slate-500">{txt("login.fp_ask_hint")}</p>
          <div className="space-y-1.5"><Label htmlFor="fp-id">{txt("login.fp_identifier")}</Label>
            <Input id="fp-id" autoCapitalize="none" className="h-12" value={ident} onChange={(e) => setIdent(e.target.value)} data-testid="forgot-identifier-input" /></div>
          {err && <p className="text-sm font-medium text-rose-600" data-testid="forgot-error">{err}</p>}
          <button disabled={!ident.trim() || busy} className={btn} data-testid="forgot-send-code">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{txt("login.fp_send_code")}</button>
        </form>
      ) : (
        <form onSubmit={reset} className="space-y-4">
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="forgot-sent">{msg}</p>
          <div className="space-y-1.5"><Label htmlFor="fp-code">{txt("login.fp_code")}</Label>
            <Input id="fp-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} className="money h-12 tracking-[0.4em]" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="forgot-code-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="fp-pw">{txt("login.fp_new_password")}</Label>
            <Input id="fp-pw" type="password" autoComplete="new-password" className="h-12" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="forgot-password-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="fp-pw2">{txt("login.fp_confirm_password")}</Label>
            <Input id="fp-pw2" type="password" autoComplete="new-password" className="h-12" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="forgot-password-confirm-input" /></div>
          {err && <p className="text-sm font-medium text-rose-600" data-testid="forgot-error">{err}</p>}
          <button disabled={code.length !== 6 || !pw || busy} className={btn} data-testid="forgot-reset-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{txt("login.fp_set_password")}</button>
          <button type="button" onClick={() => { setStep("ask"); setCode(""); setErr(""); }} className="w-full text-sm font-semibold text-slate-500 underline" data-testid="forgot-new-code">{txt("login.fp_new_code")}</button>
        </form>
      )}
    </div>
  );
}
